import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Loader2,
  PartyPopper,
  Building2,
  Shield,
  ArrowRight,
  Sparkles,
  Users,
  HelpCircle,
  Send,
  CheckCircle2,
  Plus,
} from "lucide-react";

interface WelcomeState {
  workspaceId: string;
  workspaceName: string;
  role: string;
}

interface SessionInfo {
  id: string;
  summary: string | null;
  decision_type: string | null;
  phase: number;
  created_at: string;
}

export default function Welcome() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const routerState = location.state as WelcomeState | null;

  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [memberCount, setMemberCount] = useState(0);
  const [workspaceId, setWorkspaceId] = useState<string | null>(routerState?.workspaceId || null);
  const [workspaceName, setWorkspaceName] = useState<string>(routerState?.workspaceName || "");
  const [userRole, setUserRole] = useState<string>(routerState?.role || "member");
  const [requestMessage, setRequestMessage] = useState("");
  const [sendingRequest, setSendingRequest] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [mySessionCount, setMySessionCount] = useState<number | null>(null);

  // If no router state, load workspace info from DB
  useEffect(() => {
    if (routerState || authLoading || !user) return;

    async function loadFromDb() {
      const { data: membership } = await supabase
        .from("workspace_members")
        .select("workspace_id, role, workspaces(id, name)")
        .eq("user_id", user!.id)
        .eq("seat_status", "active")
        .limit(1)
        .single();

      if (!membership) {
        navigate("/onboarding", { replace: true });
        return;
      }

      const ws = membership.workspaces as any;
      setWorkspaceId(membership.workspace_id);
      setWorkspaceName(ws?.name || "");
      setUserRole(membership.role);
    }

    loadFromDb();
  }, [routerState, authLoading, user, navigate]);

  // Load workspace sessions and member count
  useEffect(() => {
    if (!workspaceId) return;

    async function loadWorkspaceInfo() {
      try {
        const [sessionsRes, countRes, myCountRes] = await Promise.all([
          supabase
            .from("decision_sessions")
            .select("id, summary, decision_type, phase, created_at")
            .eq("workspace_id", workspaceId!)
            .neq("subphase", "3.4")
            .order("updated_at", { ascending: false })
            .limit(5),
          supabase
            .from("workspace_members")
            .select("id", { count: "exact", head: true })
            .eq("workspace_id", workspaceId!)
            .eq("seat_status", "active"),
          user
            ? supabase
                .from("decision_participants")
                .select("id", { count: "exact", head: true })
                .eq("user_id", user.id)
            : Promise.resolve({ count: 0 }),
        ]);

        setSessions(sessionsRes.data || []);
        setMemberCount(countRes.count || 0);
        setMySessionCount(myCountRes.count || 0);

        // Check if user already sent a pending request
        if (user) {
          const { data: existing } = await supabase
            .from("access_requests")
            .select("id")
            .eq("workspace_id", workspaceId!)
            .eq("requested_by", user.id)
            .eq("status", "pending")
            .limit(1);
          if (existing && existing.length > 0) {
            setRequestSent(true);
          }
        }
      } catch (err) {
        console.error("Failed to load workspace info:", err);
      } finally {
        setLoading(false);
      }
    }

    loadWorkspaceInfo();
  }, [workspaceId, user]);

  const handleSendRequest = async () => {
    if (!user || !workspaceId) return;
    setSendingRequest(true);
    try {
      const { error } = await supabase.from("access_requests").insert({
        workspace_id: workspaceId,
        requested_by: user.id,
        message: requestMessage.trim() || null,
      });

      if (error) throw error;

      setRequestSent(true);
      toast({
        title: "Anfrage gesendet",
        description: "Ein Admin wird dich zu relevanten Entscheidungen hinzufügen.",
      });
    } catch (err) {
      console.error("Failed to send request:", err);
      toast({
        title: "Fehler",
        description: "Die Anfrage konnte nicht gesendet werden.",
        variant: "destructive",
      });
    } finally {
      setSendingRequest(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!workspaceId) return null;

  const roleLabel =
    userRole === "owner" ? "Owner" : userRole === "admin" ? "Admin" : "Mitglied";
  const roleDescription =
    userRole === "admin"
      ? "Du kannst Team-Mitglieder verwalten und Einladungen versenden."
      : userRole === "owner"
      ? "Du hast vollen Zugriff auf den Workspace."
      : "Du kannst an Entscheidungen teilnehmen und eigene starten.";

  const canCreateDecision = ["owner", "admin", "member"].includes(userRole);

  const phaseLabel = (phase: number) => {
    switch (phase) {
      case 1:
        return "Analyse";
      case 2:
        return "Optionen";
      case 3:
        return "Abschluss";
      default:
        return `Phase ${phase}`;
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-2xl w-full space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 mb-2">
            <PartyPopper className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-serif font-semibold">
            Willkommen bei {workspaceName}!
          </h1>
          <p className="text-muted-foreground text-lg">
            Du bist jetzt Teil des Teams. Hier ist dein Überblick.
          </p>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="border-border/50">
            <CardContent className="p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Workspace</p>
                <p className="font-semibold">{workspaceName}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  <Users className="h-3 w-3 inline mr-1" />
                  {memberCount} {memberCount === 1 ? "Mitglied" : "Mitglieder"}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/50">
            <CardContent className="p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Deine Rolle</p>
                <p className="font-semibold">{roleLabel}</p>
                <p className="text-xs text-muted-foreground mt-1">{roleDescription}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Workspace vs Entscheidung Explainer */}
        <Card className="border-border/50 bg-muted/30">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <HelpCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Workspace vs. Entscheidung</h3>
                <div className="text-xs text-muted-foreground space-y-1.5 leading-relaxed">
                  <p>
                    <span className="font-medium text-foreground">Workspace</span> = Dein Team.
                    Alle Mitglieder teilen sich den gleichen Workspace und können eingeladen werden.
                  </p>
                  <p>
                    <span className="font-medium text-foreground">Entscheidung (Session)</span> = Ein konkreter Entscheidungsprozess.
                    Teilnehmer werden pro Session zugewiesen — nicht jeder sieht alles.
                  </p>
                  <p className="text-primary/80">
                    Du siehst nur Entscheidungen, zu denen du explizit hinzugefügt wurdest.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Sessions or Empty State */}
        {sessions.length > 0 ? (
          <Card className="border-border/50">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <h2 className="font-semibold">Laufende Entscheidungen im Workspace</h2>
                <Badge variant="secondary" className="text-xs">
                  {sessions.length}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {mySessionCount && mySessionCount > 0
                  ? `Du bist bereits an ${mySessionCount} Entscheidung${mySessionCount > 1 ? "en" : ""} beteiligt.`
                  : "Sobald du als Participant hinzugefügt wirst, erscheinen sie in deinem Dashboard."}
              </p>
              <div className="space-y-2">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-background"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {session.summary || session.decision_type || "Neue Entscheidung"}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs flex-shrink-0 ml-2">
                      {phaseLabel(session.phase)}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border/50">
            <CardContent className="p-5 text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                Es gibt noch keine laufenden Entscheidungen im Workspace.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Request Access (only if user has 0 sessions) */}
        {mySessionCount === 0 && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-5 space-y-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Send className="h-4 w-4 text-primary" />
                Zugriff auf Entscheidungen anfragen
              </h3>
              <p className="text-xs text-muted-foreground">
                Ein Admin oder Owner kann dich zu bestehenden Entscheidungen hinzufügen.
              </p>
              {requestSent ? (
                <div className="flex items-center gap-2 text-sm text-primary">
                  <CheckCircle2 className="h-4 w-4" />
                  Anfrage wurde gesendet — ein Admin wird informiert.
                </div>
              ) : (
                <div className="space-y-2">
                  <Textarea
                    placeholder="Optionale Nachricht: z.B. 'Ich arbeite am Projekt X und benötige Zugriff auf die Strategie-Entscheidung.'"
                    value={requestMessage}
                    onChange={(e) => setRequestMessage(e.target.value)}
                    className="text-sm resize-none h-20"
                  />
                  <Button
                    size="sm"
                    onClick={handleSendRequest}
                    disabled={sendingRequest}
                    className="w-full sm:w-auto"
                  >
                    {sendingRequest ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Anfrage senden
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3">
          {canCreateDecision && (
            <Button
              variant="outline"
              className="h-12"
              onClick={() => navigate("/dashboard")}
            >
              <Plus className="h-4 w-4 mr-2" />
              Neue Entscheidung starten
            </Button>
          )}
          <Button
            className="flex-1 h-12 text-base font-semibold"
            onClick={() => navigate("/dashboard")}
          >
            Zum Dashboard
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
